import { Button } from '@heidi/design-system/button';
import type { IconProps } from '@heidi/design-system/icons';
import { Link } from '@heidi/design-system/link';
import { TypographyCaption } from '@heidi/design-system/typography';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { Loader2 } from 'lucide-react';
import { type ComponentType, type Ref, type RefObject } from 'react';
import { FormattedMessage } from 'react-intl';

import { getCommerceBillingUiPorts } from '../../settings/billing/billing-ui-ports';
import { TeamTrialUpgradeRequestButton } from '../team-trial-upgrade-request-button/team-trial-upgrade-request-button';
import { trialRecapModalMessages } from '../trial-recap-modal/messages';
import { trialUpgradeRequestCallbackModalMessages } from '../trial-upgrade-request-callback-modal/messages';
import { clinicianTrialEndedModalMessages } from './clinician-trial-ended-modal-messages';

type TierIconComponent = ComponentType<IconProps>;

type ClinicianTrialEndedModalFooterProps = {
  isClinicianPlan: boolean;
  isNonAdmin: boolean;
  showContactSalesCTA: boolean;
  hasCallbackRequested: boolean;
  /**
   * `true` while the direct-to-Stripe upgrade is in flight (either creating a
   * Stripe Checkout session or fetching the billing-portal deeplink). Disables
   * the primary upgrade button and swaps its label for a spinner so users
   * don't double-click before the redirect kicks in.
   */
  isUpgradePending: boolean;
  termsOfUseUrl: string;
  privacyPolicyUrl: string;
  upgradePrimaryRef: RefObject<HTMLButtonElement | null>;
  TierIcon: TierIconComponent | undefined;
  onUpgradeClinician: () => void;
  onUpgradePractice: () => void;
  onRequestToUpgrade: () => void;
  onRemain: () => void;
  onPracticeTalkToUs: () => void;
  onContactSalesClick: () => void;
};

export function ClinicianTrialEndedModalFooter({
  isClinicianPlan,
  isNonAdmin,
  showContactSalesCTA,
  hasCallbackRequested,
  isUpgradePending,
  termsOfUseUrl,
  privacyPolicyUrl,
  upgradePrimaryRef,
  TierIcon,
  onUpgradeClinician,
  onUpgradePractice,
  onRequestToUpgrade,
  onRemain,
  onPracticeTalkToUs,
  onContactSalesClick,
}: ClinicianTrialEndedModalFooterProps) {
  if (getCommerceBillingUiPorts().isAppleCompliantBuild()) {
    return null;
  }

  // Non-admin team members: single orange "Request to upgrade" CTA — opens the
  // existing TeamTrialUpgradeRequestModal so they can ask their admin to upgrade.
  // No secondary, no disclaimer — matches the Figma's lean non-admin layout.
  if (isNonAdmin) {
    return (
      <TeamTrialUpgradeRequestButton
        ref={upgradePrimaryRef as Ref<HTMLButtonElement>}
        plan={
          isClinicianPlan
            ? SubscriptionTier.CLINICIAN_PRO
            : SubscriptionTier.CLINICIAN_PRO_PLUS
        }
        requestToUpgradeMessage={clinicianTrialEndedModalMessages.requestToUpgradeCta}
        requestedTooltipMessage={
          clinicianTrialEndedModalMessages.requestToUpgradeAlreadySentTooltip
        }
        onOpenTeamRequestModal={onRequestToUpgrade}
      />
    );
  }

  // Contact-sales callback CTA: replaces the upgrade button when the user
  // should be routed to sales instead of self-upgrading (e.g. Practice admin
  // on a non–Stripe-managed team). After the user submits a callback request
  // the button stays mounted but goes disabled with the "Callback requested"
  // label — see GRO-3062 for the full flow.
  if (showContactSalesCTA) {
    return (
      <Button
        ref={upgradePrimaryRef as Ref<HTMLButtonElement>}
        type="button"
        variant={isClinicianPlan ? 'pro' : 'pro_plus'}
        size="lg"
        disabled={hasCallbackRequested}
        onClick={onContactSalesClick}
        className="w-full"
      >
        {!hasCallbackRequested && TierIcon && <TierIcon size={16} aria-hidden />}
        <FormattedMessage
          {...(hasCallbackRequested
            ? trialUpgradeRequestCallbackModalMessages.callbackRequestedCta
            : isClinicianPlan
              ? trialRecapModalMessages.talkToUsKeepPlanCtaClinician
              : trialRecapModalMessages.talkToUsKeepPlanCtaPractice)}
        />
      </Button>
    );
  }

  // Solo Clinician / Practice admin (Stripe-managed): primary upgrade CTA + secondary
  // "Remain on Scribe Plus / Team" + Terms/Privacy disclaimer. Practice admins also
  // get a "talk to a Heidi sales expert — get in touch" sub-line.
  const upgradeMessage = isClinicianPlan
    ? clinicianTrialEndedModalMessages.upgradeToClinicianCta
    : clinicianTrialEndedModalMessages.upgradeToPracticeCta;
  const remainMessage = isClinicianPlan
    ? clinicianTrialEndedModalMessages.remainOnScribePlusCta
    : clinicianTrialEndedModalMessages.remainOnScribeTeamCta;
  const disclaimerMessage = isClinicianPlan
    ? clinicianTrialEndedModalMessages.disclaimerSoloClinician
    : clinicianTrialEndedModalMessages.disclaimerPracticeAdmin;

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="flex w-full flex-col gap-2">
        <Button
          ref={upgradePrimaryRef as Ref<HTMLButtonElement>}
          type="button"
          variant={isClinicianPlan ? 'pro' : 'pro_plus'}
          size="lg"
          disabled={isUpgradePending}
          className="w-full"
          onClick={isClinicianPlan ? onUpgradeClinician : onUpgradePractice}
        >
          {isUpgradePending ? (
            <>
              <Loader2 className="animate-spin" aria-hidden />
              {/* Keep the upgrade copy in the accessible name so screen readers
                  and tests don't lose context while the Stripe redirect is in
                  flight — the spinner is the only visual cue, but the label
                  remains the button's `aria-label`-equivalent via the text node. */}
              <span className="sr-only">
                <FormattedMessage {...upgradeMessage} />
              </span>
            </>
          ) : (
            <>
              {TierIcon && <TierIcon size={16} aria-hidden />}
              <FormattedMessage {...upgradeMessage} />
            </>
          )}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="lg"
          className="w-full"
          onClick={onRemain}
        >
          <FormattedMessage {...remainMessage} />
        </Button>
      </div>
      <TypographyCaption className="text-center text-text-secondary">
        <FormattedMessage
          {...disclaimerMessage}
          values={{
            terms: (chunks) => (
              <Link
                variant="inline"
                tone="default"
                href={termsOfUseUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {chunks}
              </Link>
            ),
            privacy: (chunks) => (
              <Link
                variant="inline"
                tone="default"
                href={privacyPolicyUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {chunks}
              </Link>
            ),
          }}
        />
        {!isClinicianPlan && (
          <>
            {' '}
            <FormattedMessage
              {...clinicianTrialEndedModalMessages.salesContactPrompt}
              values={{
                link: (chunks) => (
                  <Link
                    variant="inline"
                    tone="default"
                    href={getCommerceBillingUiPorts().billingUrls.GET_IN_TOUCH_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={onPracticeTalkToUs}
                  >
                    {chunks}
                  </Link>
                ),
              }}
            />
          </>
        )}
      </TypographyCaption>
    </div>
  );
}
